/**
 * Die lokale Outbox der Monteur-App — `PWA-003`.
 *
 * **Was dieses Modul ist und was nicht.** `packages/sync` entscheidet, *ob*
 * eine Operation angenommen werden darf — es ist reines TypeScript ohne
 * Speicher, Netz und Verschlüsselung. Was hier liegt, ist die andere Hälfte:
 * wo die Operationen bleiben, in welcher Reihenfolge sie zugestellt werden,
 * und wie sich der Bildschirm verhält, solange sie unterwegs sind.
 *
 * **Der Zustand liegt als Auflage über dem Bestand, nicht darin.** Der
 * Demobestand aus `@hwc/demo-data` ist unveränderlich, und das soll er
 * bleiben: Er ist die Projektion des Servers, und der Server weiß von einer
 * Änderung erst, wenn sie zugestellt wurde. Was der Monteur im Funkloch tut,
 * gehört genau bis dahin ihm allein — `07` §7.9 nennt das „wartet auf Upload".
 * Zwei getrennte Schichten machen den Unterschied sichtbar, statt ihn zu
 * verwischen.
 *
 * **Die Verschlüsselung fehlt, und das steht hier.** `05` §5.6 verlangt einen
 * verschlüsselten Offlinecache; `PWA-003` baut ihn. Was hier liegt, ist
 * `localStorage` im Klartext — brauchbar für einen Demonstrationsstand auf
 * einem Bürorechner, untauglich für ein Monteurhandy mit Kundendaten. Der
 * Austausch betrifft diese Datei und keine andere: Alles darüber spricht nur
 * mit `lade` und `sichere`.
 */

import type { EinsatzZustand } from "@hwc/sync";
import { pruefeOperation } from "@hwc/sync";
import type { Befund, Offlineoperation, Serverstand } from "@hwc/sync";

/** Was ein Einsatz lokal an Änderungen trägt, solange nichts zugestellt ist. */
export interface Einsatzauflage {
  readonly zustand?: EinsatzZustand;
  /** Arbeitsschritte, die lokal abgehakt wurden — Ref als Zeichenkette. */
  readonly erledigteSchritte: readonly string[];
  readonly notiz?: string;
  /**
   * Begründungen zu Pflichtschritten, die offen bleiben — Schritt-Id auf Text.
   *
   * `07` §7.8 nennt als Abschlussbedingung „Checkliste vollständig **oder
   * begründete Ausnahme**". Ohne dieses Feld gab es die zweite Hälfte nicht:
   * Der Abschlussknopf war gesperrt, solange ein Pflichtschritt offen war, und
   * der Hinweis daneben versprach eine Ausnahme, zu der kein Weg führte.
   *
   * Das ist nicht bloß unbequem — es erzeugt falsche Daten. Ein Monteur, der
   * seinen Bericht nicht abschicken kann, hakt den Pflichtschritt ab, den er
   * nicht ausführen konnte. Die Sperre produziert genau die Angabe, die sie
   * verhindern soll.
   *
   * Optional, damit ein Stand aus einer früheren Fassung lesbar bleibt.
   */
  readonly ausnahmen?: Readonly<Record<string, string>>;
  /** Lokal erfasste Zeitposten. */
  readonly zeiten: readonly LokalerZeitposten[];
  readonly material: readonly LokalerMaterialposten[];
  readonly fotos: readonly LokalesFoto[];
  /**
   * Chatbeiträge, die noch nicht draußen sind.
   *
   * Optional, damit ein gespeicherter Stand aus einer früheren Fassung weiter
   * lesbar bleibt — `lade` setzt keine Felder nach, die es damals nicht gab.
   */
  readonly beitraege?: readonly LokalerBeitrag[];
}

export interface LokalerBeitrag {
  readonly id: string;
  /** Der Raum in seiner Zeichenkettenform. */
  readonly raum: string;
  readonly text: string;
  readonly gesendet: string;
}

export interface LokalerZeitposten {
  readonly id: string;
  readonly art: string;
  readonly von: string;
  readonly bis: string;
  readonly abrechenbar: boolean;
  readonly notiz?: string;
}

export interface LokalerMaterialposten {
  readonly id: string;
  readonly bezeichnung: string;
  readonly menge: number;
  readonly einheit: string;
  readonly abrechenbar: boolean;
}

export interface LokalesFoto {
  readonly id: string;
  readonly name: string;
  readonly aufgenommen: string;
}

/** Der gesamte lokale Stand: Auflagen je Einsatz plus die Warteschlange. */
export interface Lokalstand {
  readonly auflagen: Readonly<Record<string, Einsatzauflage>>;
  readonly warteschlange: readonly Offlineoperation[];
  /** Operationen, die abgelehnt wurden oder in den Konflikt liefen. */
  readonly befunde: readonly { readonly op: Offlineoperation; readonly befund: Befund }[];
  /**
   * Berichtsentwürfe je Einsatz — `07` §7.8.
   *
   * **Hier und nicht nur im Arbeitsspeicher.** Sie lagen zuerst im
   * Anwendungszustand, und das war genau so lange richtig, bis jemand die
   * Seite neu lud: Die Erfassung überlebte, der daraus erzeugte Entwurf
   * nicht — und der Monteur schickte einen Einsatz ab, zu dem kein Bericht
   * mehr existierte. Gemessen am 05.09.2026.
   *
   * Der Typ ist bewusst `unknown`: Dieses Modul kennt die Outbox, nicht das
   * Fachmodell. Ein Import von `Servicebericht` machte aus einem Speicher
   * eine Fachbibliothek.
   */
  readonly entwuerfe: Readonly<Record<string, unknown>>;
}

export const LEERER_STAND: Lokalstand = {
  auflagen: {},
  warteschlange: [],
  befunde: [],
  entwuerfe: {},
};

export const LEERE_AUFLAGE: Einsatzauflage = {
  erledigteSchritte: [],
  zeiten: [],
  material: [],
  fotos: [],
};

const SCHLUESSEL = "hwc.monteur.outbox";

/**
 * Liest den lokalen Stand.
 *
 * Jeder Fehler endet beim leeren Stand statt bei einem Absturz. Der Grund ist
 * derselbe wie überall dort, wo `localStorage` im Spiel ist: Ein privates
 * Fenster, blockierte Website-Daten oder ein Stand aus einer früheren Fassung
 * dürfen nicht dazu führen, dass ein Monteur vor einem weißen Bildschirm
 * steht. Er verliert dann seine ungesendeten Eingaben — schlimm genug, aber
 * besser als eine App, die gar nicht startet.
 */
export function lade(): Lokalstand {
  try {
    const roh = window.localStorage.getItem(SCHLUESSEL);
    if (!roh) return LEERER_STAND;
    const gelesen = JSON.parse(roh) as Partial<Lokalstand>;
    return {
      auflagen: gelesen.auflagen ?? {},
      warteschlange: gelesen.warteschlange ?? [],
      befunde: gelesen.befunde ?? [],
      entwuerfe: gelesen.entwuerfe ?? {},
    };
  } catch {
    return LEERER_STAND;
  }
}

export function sichere(stand: Lokalstand): void {
  try {
    window.localStorage.setItem(SCHLUESSEL, JSON.stringify(stand));
  } catch {
    // Nicht sichern können ist kein Fehler, den der Monteur mitten in der
    // Arbeit sehen muss. Sichtbar wird es ohnehin: Die Warteschlange steht
    // im Bild, und nach einem Neustart wäre sie leer.
  }
}

export function loesche(): void {
  try {
    window.localStorage.removeItem(SCHLUESSEL);
  } catch {
    // s.o.
  }
}

/** Die Auflage eines Einsatzes, oder eine leere. */
export function auflageVon(stand: Lokalstand, einsatzRef: string): Einsatzauflage {
  return stand.auflagen[einsatzRef] ?? LEERE_AUFLAGE;
}

/**
 * Eine lokale Kennung, die vor dem ersten Netzkontakt vergeben wird.
 *
 * `crypto.randomUUID` ist seit Jahren überall da, wo diese App läuft — aber
 * nur über HTTPS oder auf `localhost`. Auf einem Baustellentablet, das den
 * Entwicklungsserver über die LAN-Adresse aufruft, fehlt es, und dort fiele
 * es zuerst auf. Der Rückfall ist nicht kryptografisch und muss es nicht
 * sein: Er unterscheidet Operationen desselben Geräts, mehr verlangt
 * `04` §4.12 von der lokalen UUID nicht.
 */
export function neueKennung(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `lok-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Legt eine Operation an — ohne sie zuzustellen.
 *
 * Der `idempotencyKey` beschreibt den **Vorgang**, nicht das Gerät: Zwei
 * Geräte, die denselben Arbeitsschritt abhaken, erzeugen zwei ids und
 * denselben Schlüssel, und genau daran erkennt die Zustellung die Dopplung
 * (Kommentar in `packages/sync/src/outbox.ts`).
 */
export function baueOperation(args: {
  readonly tenantId: string;
  readonly actorRef: string;
  readonly resourceRef: string;
  readonly baselineVersion: number;
  readonly mutation: Offlineoperation["mutation"];
  readonly nutzlast: unknown;
  readonly vorgang: string;
}): Offlineoperation {
  return {
    id: neueKennung(),
    tenantId: args.tenantId,
    actorRef: args.actorRef,
    resourceRef: args.resourceRef,
    baselineVersion: args.baselineVersion,
    mutation: args.mutation,
    /**
     * Im Betrieb verschlüsselt (`05` §5.6); hier Klartext, und der Kommentar
     * am Dateikopf sagt, warum das für einen Demostand geht.
     *
     * **Bei `zustand` ist die Nutzlast der Zustandsname selbst**, nicht ein
     * JSON-Objekt. So liest `zielZustand()` in `packages/sync` sie, und der
     * Kommentar dort begründet es: eine schlichte Zeichenkette, „damit dieses
     * Modul kein Serialisierungsformat festlegt, das ein anderes Paket dann
     * tragen müsste".
     *
     * Der erste Entwurf schickte `{von, nach}` als JSON. Die Prüfung lehnte
     * jeden Wechsel mit „nennt keinen gültigen Zielzustand" ab — eine
     * korrekte Absage auf eine falsch gestellte Frage.
     */
    payload:
      typeof args.nutzlast === "string"
        ? args.nutzlast
        : JSON.stringify(args.nutzlast),
    erstelltAm: new Date().toISOString(),
    retryZaehler: 0,
    idempotencyKey: `${args.resourceRef}#${args.vorgang}`,
  };
}

/**
 * Stellt die Warteschlange zu — gegen einen angenommenen Serverstand.
 *
 * **Es gibt keinen Server.** Was hier passiert, ist die Zustellung gegen den
 * Stand, den der Aufrufer beschreibt, durch dieselbe Prüfung, die später der
 * echte Empfänger anwendet — `pruefeOperation` aus `packages/sync`. Das ist
 * der Punkt: Die Regeln aus `04` §4.12 sind schon jetzt wirksam und nicht
 * erst, wenn ein Dienst dahintersteht. Ein Zustandswechsel, der nach ADR-045
 * nicht zulässig ist, wird auch hier abgelehnt.
 *
 * Zugestellt wird **in Reihenfolge**. Ein Einsatz geht `geplant → anfahrt →
 * vor-ort`; würde die zweite Operation vor der ersten ankommen, lehnte der
 * Automat sie zu Recht ab. Die Warteschlange ist deshalb eine Schlange und
 * keine Menge.
 */
export function stelleZu(
  stand: Lokalstand,
  serverstand: (op: Offlineoperation) => Serverstand,
  /**
   * Wie sich der Serverstand durch eine angenommene Operation ändert.
   *
   * **Ohne diesen Rückgriff scheitert jede Kette ab der zweiten Operation.**
   * Gemessen am 05.09.2026: Ein Einsatz wurde offline durch `anfahrt`,
   * `vor-ort`, `abschluss` und `uebermittelt` geführt; beim Senden lehnte die
   * Prüfung alles außer dem ersten Wechsel ab. Der Grund war nicht die
   * Prüfung, sondern die Auskunft, die sie bekam: Der Stand wurde für jede
   * Operation neu aus dem unveränderten Bestand gelesen und blieb deshalb bei
   * `geplant` stehen. Aus Sicht des Servers kam viermal ein Wechsel von
   * `geplant` — und dreimal war er unzulässig.
   *
   * Ein echter Empfänger hat dieses Problem nicht: Er verarbeitet eine
   * Operation, schreibt den neuen Stand und liest ihn für die nächste. Genau
   * das bildet dieser Rückgriff nach, und er gehört deshalb zur Zustellung
   * und nicht zum Aufrufer.
   */
  fortschreiben: (stand: Serverstand, op: Offlineoperation) => Serverstand,
): {
  readonly stand: Lokalstand;
  readonly zugestellt: readonly Offlineoperation[];
  readonly befunde: readonly { readonly op: Offlineoperation; readonly befund: Befund }[];
} {
  const zugestellt: Offlineoperation[] = [];
  const befunde: { op: Offlineoperation; befund: Befund }[] = [];
  const bleibt: Offlineoperation[] = [];

  // Der laufende Stand je Ressource. Er wird beim ersten Zugriff aus der
  // Auskunft geholt und danach fortgeschrieben — nicht jedes Mal neu gelesen.
  const staende = new Map<string, Serverstand>();

  for (const op of stand.warteschlange) {
    const vorher = staende.get(op.resourceRef) ?? serverstand(op);
    const befund = pruefeOperation(op, vorher);
    if (befund.entscheidung === "annehmen" || befund.entscheidung === "doppelt") {
      zugestellt.push(op);
      staende.set(op.resourceRef, fortschreiben(vorher, op));
    } else {
      staende.set(op.resourceRef, vorher);
      // Konflikt und Ablehnung bleiben **nicht** in der Warteschlange: Ein
      // erneuter Versuch ändert nichts, und eine Schlange, die sich nie
      // leert, verdeckt die nächste echte Störung. Sie werden zu Befunden,
      // und die stehen sichtbar in „Synchronisationsprobleme" (`07` §7.7).
      befunde.push({ op, befund });
    }
  }

  /**
   * `lokalLoeschen` **wird ausgeführt**, nicht nur gemeldet.
   *
   * `04` §4.12 verlangt es nur in einem Fall: „fehlende Berechtigung nach
   * Wiederverbindung stoppt die Operation und entfernt lokal nicht mehr
   * erlaubte Inhalte." Bei jeder anderen Ablehnung gilt das Gegenteil — der
   * Monteur hat gearbeitet, und seine Erfassung verschwindet nicht, weil ein
   * Zustandswechsel nicht durchging. `pruefeOperation` unterscheidet das
   * bereits; hier stand bis zum 05.09.2026 nur die Meldung.
   *
   * Aufgefallen ist es im Gegentest: Ein Einsatz eines anderen Monteurs wurde
   * mit „Keine Berechtigung" abgelehnt, die Anzeige sagte „Lokale Erfassung
   * wurde entfernt" — und im Speicher stand die Änderung weiter. Eine Zusage,
   * die niemand einlöst, ist schlimmer als keine: Sie beruhigt genau die
   * Prüfung, die den Fall finden sollte.
   */
  const zuLoeschen = new Set(
    befunde.filter((b) => b.befund.lokalLoeschen).map((b) => b.op.resourceRef),
  );

  /**
   * **Ein zugestellter Zustand gehört nicht mehr in die Auflage.**
   *
   * Die Auflage ist das, was der Server noch nicht weiß. Sobald er es weiß,
   * überschattet sie ihn nur noch — und zwar dauerhaft: Der Monteur sah am
   * 05.09.2026 seinen Einsatz weiter auf `uebermittelt`, obwohl das Büro ihn
   * längst zurückgegeben hatte, weil die eigene alte Angabe gewann.
   *
   * Geräumt wird **nur der Zustand**, nicht die Erfassung. Zeiten, Material
   * und Fotos bleiben stehen: Sie sind additiv, der Server hat sie
   * übernommen, und sie verschwinden zu lassen sähe für den Monteur aus wie
   * Datenverlust.
   */
  const zustandZugestellt = new Set(
    zugestellt.filter((op) => op.mutation === "zustand").map((op) => op.resourceRef),
  );
  const auflagen = Object.fromEntries(
    Object.entries(stand.auflagen)
      .filter(([ref]) => !zuLoeschen.has(ref))
      .map(([ref, auflage]) => {
        if (!zustandZugestellt.has(ref)) return [ref, auflage] as const;
        // Den Zustand herausnehmen, den Rest behalten. Ohne Destrukturierung,
        // weil der Linter die ungenutzte Bindung sonst zu Recht anmahnt — und
        // eine Ausnahmeregel dafuer waere teurer als drei Zeilen.
        const rest: Einsatzauflage = {
          erledigteSchritte: auflage.erledigteSchritte,
          zeiten: auflage.zeiten,
          material: auflage.material,
          fotos: auflage.fotos,
          ...(auflage.notiz !== undefined ? { notiz: auflage.notiz } : {}),
          ...(auflage.beitraege !== undefined ? { beitraege: auflage.beitraege } : {}),
        };
        return [ref, rest] as const;
      }),
  );

  return {
    stand: {
      ...stand,
      auflagen,
      warteschlange: bleibt,
      befunde: [...stand.befunde, ...befunde],
    },
    zugestellt,
    befunde,
  };
}
